import React, {useEffect, useState, useRef, ChangeEvent} from 'react'
import {Accordion, Stack, Button} from 'react-bootstrap'
import {StepProps} from '../IAppModels'
import uuid from 'react-uuid'
import deleteButton from '../../../../images/deleteButton.svg'
import editButton from '../../../../images/editButton.svg'
import './accordianStyle.css'
import {omit} from 'lodash'

type AccordionEventKey = any

interface BrandAdmin {
  id: string
  emailId: string
  newadminflag?: boolean
  isDelete?: boolean
}

interface Brand {
  name: string
  id: string
  brandAdmin: BrandAdmin[]
  newbrandflag?: boolean
  isDelete?: boolean
}

const Step7 = ({
  data,
  updateData,
  hasError,
  setHasError,
  isDisabled,
  style,
  validation,
  callBack,
  setBrandsInfo,
  setEmailDisabled,
}: StepProps) => {
  const [brands, setBrands] = useState<Brand[]>([])
  const [newAdminName, setNewAdminName] = useState<string>('')
  const [showName, setShowName] = useState<boolean>(false)
  const [activeEventKey, setActiveEventKey] = useState<AccordionEventKey>(null)
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null)
  const [brandErrors, setBrandErrors] = useState<{[key: string]: string}>({}) // Track errors per brand
  const [brandduplicateErrors, setBrandduplicateErrors] = useState('') // Track errors per brand

  const scrollEndRef = useRef<HTMLDivElement>(null)
  const inputRefs = useRef<{[key: string]: HTMLDivElement | null}>({})

  useEffect(() => {
    if (data?.brandInfo) {
      setBrands(data?.brandInfo)
    }
  }, [data?.brandInfo])

  useEffect(() => {
    const cleanedBrands = brands.map((brand) => {
      let cleanedBrand = brand.newbrandflag
        ? omit(brand, ['id', 'newbrandflag', '__typename'])
        : omit(brand, '__typename')

      const cleanedBrandAdmin =
        cleanedBrand?.brandAdmin &&
        cleanedBrand?.brandAdmin.map((admin) =>
          admin.newadminflag
            ? omit(admin, ['id', 'newadminflag', '__typename'])
            : omit(admin, '__typename')
        )

      return {
        ...cleanedBrand,
        brandAdmin: cleanedBrandAdmin,
      }
    })

    setBrandsInfo(cleanedBrands)
  }, [brands])

  const handleAdd = () => {
    setShowName(true)
    scrollEndRef.current?.scrollIntoView({behavior: 'smooth'})
  }

  const handleSave = () => {
    if (!newAdminName.trim()) {
      return
    }

    const filteredBrands = brands.filter((brand) => !brand.isDelete)
    if (filteredBrands.some((brand) => brand.name === newAdminName)) {
      setBrandduplicateErrors('Brand name already exists')
      setEmailDisabled(true)
      return
    }

    const newBrand: Brand = {
      name: newAdminName,
      id: uuid(),
      brandAdmin: [],
      newbrandflag: true,
    }
    setBrands([...brands, newBrand])
    setNewAdminName('')
    setShowName(false)
    setBrandErrors((prevErrors) => ({
      ...prevErrors,
      new: '',
    }))
    setEmailDisabled(false)
  }

  const handleDelete = (id: string) => {
    setBrands(brands.map((brand) => (brand?.id === id ? {...brand, isDelete: true} : brand)))
  }

  const handleDeleteEmail = (brandId: string, adminId: string) => {
    setBrands(
      brands.map((brand) => {
        if (brand?.id === brandId) {
          const updatedBrandAdmin = brand.brandAdmin
            .map((admin) => (admin?.id === adminId ? {...admin, isDelete: true} : admin))
            // .filter((admin) => admin?.newadminflag !== true)

          return {
            ...brand,
            brandAdmin: updatedBrandAdmin,
          }
        } 
        return brand
      })
    )

    // Clear the error associated with the deleted email
    setEmailErrors((prevErrors) => {
      const newErrors = {...prevErrors}
      if (newErrors[brandId]) {
        delete newErrors[brandId][adminId]
        if (Object.keys(newErrors[brandId]).length === 0) {
          delete newErrors[brandId]
        }
      }

      // Disable email field if there are any errors
      const hasErrors = Object.keys(newErrors).length > 0
      setEmailDisabled(hasErrors)

      return newErrors
    })
  }

  const handleAddEmail = (brandId: string) => {
    setBrands(
      brands.map((brand) => {
        if (brand?.id === brandId) {
          const newAdmin: BrandAdmin = {
            id: uuid(),
            emailId: '',
            newadminflag: true,
          }
          return {
            ...brand,
            brandAdmin: [...brand.brandAdmin, newAdmin],
          }
        }
        return brand
      })
    )
  }
  const [emailErrors, setEmailErrors] = useState<{[brandId: string]: {[adminId: string]: string}}>(
    {}
  )

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
  const handleEmailBlur = (brandId: string, adminId: string, value: string) => {
    setEmailErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
  
      if (!validateEmail(value)) {
        if (!newErrors[brandId]) {
          newErrors[brandId] = {};
        }
        newErrors[brandId][adminId] = 'Invalid email address';
        setEmailDisabled(true);
      } else if (
        brands.some((brand) =>
          brand.brandAdmin.some(
            (admin) => admin.emailId === value && admin.id !== adminId && !admin.isDelete
          )
        )
      ) {
        if (!newErrors[brandId]) {
          newErrors[brandId] = {};
        }
        newErrors[brandId][adminId] = 'Email address already in use';
        setEmailDisabled(true);
      } else {
        if (newErrors[brandId]) {
          delete newErrors[brandId][adminId];
          if (Object.keys(newErrors[brandId]).length === 0) {
            delete newErrors[brandId];
          }
        }
        setEmailDisabled(false);
      }
  
      return newErrors;
    });
  };

  const handleEmailChange = (brandId: string, adminId: string, value: string) => {
    setBrands(
      brands.map((brand) => {
        if (brand?.id === brandId) {
          return {
            ...brand,
            brandAdmin: brand.brandAdmin.map((admin) => {
              if (admin?.id === adminId) {
                return {
                  ...admin,
                  emailId: value,
                }
              }
              return admin
            }),
          }
        }
        return brand
      })
    )
    setEmailErrors((prevErrors) => {
      const newErrors = {...prevErrors}
      if (newErrors[brandId] && newErrors[brandId][adminId]) {
        delete newErrors[brandId][adminId]
        if (Object.keys(newErrors[brandId]).length === 0) {
          delete newErrors[brandId]
        }
      }
      return newErrors
    })
  }

  const handleAccordionSelect = (eventKey: AccordionEventKey) => {
    setActiveEventKey(eventKey)
  }

  const handleEditBrandName = (brand: Brand) => {
    setEditingBrandId(brand.id)
    setTimeout(() => {
      const editableDiv = inputRefs.current[brand.id]
      if (editableDiv) {
        editableDiv.focus()
        const range = document.createRange()
        const sel = window.getSelection()
        range.setStart(editableDiv.firstChild || editableDiv, editableDiv.innerText.length)
        range.collapse(true)
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    }, 0)
  }

  const handleSaveEditedBrandName = (brandId: string, newName: any) => {
    if (!newName.trim()) {
      setBrandErrors((prevErrors) => ({
        ...prevErrors,
        [brandId]: 'Group name cannot be empty',
      }))
      setEmailDisabled(true)
      return
    }

    if (brands.some((brand) => brand.id !== brandId && brand.name === newName)) {
      setBrandErrors((prevErrors) => ({
        ...prevErrors,
        [brandId]: 'Brand name already exists',
      }))
      setEmailDisabled(true)

      return
    }

    setBrands(
      brands.map((brand) => {
        if (brand.id === brandId) {
          return {
            ...brand,
            name: newName,
          }
        }
        return brand
      })
    )
    setEditingBrandId(null)
    setEmailDisabled(false)
    setBrandErrors((prevErrors) => ({
      ...prevErrors,
      [brandId]: '',
    }))
  }

  const handleClearNewAdminName = () => {
    setBrandduplicateErrors('')
    setNewAdminName('')
    setEmailDisabled(false)
  }

  return (
    <div data-kt-stepper-element='content'>
      <div className='w-100' style={{ overflowX: 'hidden', overflowY: 'auto' }}>
        <div className='form'>
          <div className='fv-row'>
            <Stack direction='horizontal'>
              <div>
                <label className='d-flex align-items-center fs-5 fw-semibold mb-4'>
                  <span className='fw-bold fs-3'>System Admin List</span>
                </label>
              </div>
              <div className='ms-auto'>
                <Button
                  type='button'
                  className='btn btn-light-primary me-3 fs-6 fw-500 mb-4'
                  style={{width: '145px', height: '38px', padding: '3px'}}
                  onClick={handleAdd}
                >
                  Add System Admin
                </Button>
              </div>
            </Stack>
            {showName && (
              <Stack direction='horizontal' className='mb-4'>
                <div className='d-flex flex-column'>
                  <div className='mr-12px' style={{position: 'relative'}}>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      name='brandName'
                      placeholder='Enter system admin name'
                      value={newAdminName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewAdminName(e.target.value)
                      }
                    />
                    {newAdminName && (
                      <span
                        className='clear-icon'
                        onClick={handleClearNewAdminName}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: '#888',
                        }}
                      >
                        ×
                      </span>
                    )}
                  </div>
                  {brandduplicateErrors && (
                    <div
                      style={{
                        color: 'red',
                        top: '100%',
                      }}
                    >
                      {brandduplicateErrors}
                    </div>
                  )}
                </div>
                <div className='ms-auto'>
                  <Button
                    type='button'
                    className='btn btn-light-primary me-3 fs-6 fw-500'
                    style={{width: '145px', height: '38px', padding: '3px'}}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </div>
              </Stack>
            )}
            <Stack>
              <Accordion
                activeKey={activeEventKey}
                onSelect={handleAccordionSelect}
                className='accordion-custom'
              >
                {brands
                  .filter((brand) => !brand.isDelete)
                  .map((brand, index) => (
                    <div key={brand?.id} className='accordion-item-container'>
                      <div className='accordion-header-wrapper'>
                        <Accordion.Item eventKey={index.toString()} style={{width: '80%'}}>
                          <Accordion.Header>
                            <div className='d-flex flex-column'>
                              <div
                                ref={(el) => (inputRefs.current[brand.id] = el)}
                                contentEditable={editingBrandId === brand.id}
                                suppressContentEditableWarning={true}
                                onBlur={() =>
                                  handleSaveEditedBrandName(
                                    brand.id,
                                    inputRefs.current[brand.id]?.innerText
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    handleSaveEditedBrandName(
                                      brand.id,
                                      inputRefs.current[brand.id]?.innerText
                                    )

                                    const editableDiv = inputRefs.current[brand.id]
                                    if (editableDiv) {
                                      editableDiv.blur()
                                    }
                                  }
                                }}
                                className='editable-header'
                                style={{
                                  outline: 'none',
                                  cursor: editingBrandId === brand.id ? 'text' : 'pointer',
                                  position: 'relative',
                                }}
                              >
                                {brand?.name}
                              </div>
                              {brandErrors[brand.id] && (
                                <div
                                  className='error-message'
                                  style={{
                                    color: 'red',
                                    top: '100%',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {brandErrors[brand.id]}
                                </div>
                              )}
                            </div>
                            <div className='headerBlock'>
                              <span
                                className='ms-2  btn-light-primary '
                                style={{
                                  padding: 'calc(0.775rem + 1px) calc(1.5rem + 1px)',
                                }}
                              >
                                {brand?.brandAdmin.filter((admin) => !admin.isDelete && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.emailId)).length} User
                                {brand?.brandAdmin.filter((admin) => !admin.isDelete && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.emailId)).length !==
                                  1 && 's'}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {brand?.brandAdmin
                              .filter((admin) => !admin.isDelete)
                              .map((admin) => (
                                <div key={admin?.id} className='d-flex flex-column mb-2'>
                                  <div className='d-flex align-items-center'>
                                    <input
                                      type='text'
                                      className='form-control form-control-lg form-control-solid'
                                      value={admin?.emailId}
                                      placeholder='Enter user email'
                                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        handleEmailChange(brand?.id, admin?.id, e.target.value)
                                      }
                                      onBlur={(e: any) =>
                                        handleEmailBlur(brand?.id, admin?.id, e.target.value)
                                      }
                                      style={{marginRight: '10px'}}
                                    />
                                    <img
                                      src={deleteButton}
                                      alt='delete'
                                      onClick={() => handleDeleteEmail(brand?.id, admin?.id)}
                                      className='pointer'
                                    />
                                  </div>
                                  {emailErrors[brand.id]?.[admin.id] && (
                                    <div
                                      className='error-message'
                                      style={{
                                        color: 'red',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {emailErrors[brand.id][admin.id]}
                                    </div>
                                  )}
                                </div>
                              ))}

                            <Button
                              type='button'
                              className='btn btn-light-primary me-3 fs-6 fw-500 mb-4'
                              style={{width: '145px', height: '38px', padding: '3px'}}
                              onClick={() => handleAddEmail(brand?.id)}
                            >
                              Add Email
                            </Button>
                          </Accordion.Body>
                        </Accordion.Item>
                        <div className='edit-delete-buttons'>
                          <div>
                            <img
                              src={editButton}
                              alt='edit'
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditBrandName(brand)
                              }}
                              className='pointer ms-2'
                            />
                            <img
                              src={deleteButton}
                              alt='delete'
                              onClick={() => handleDelete(brand?.id)}
                              className='pointer ms-2'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </Accordion>
            </Stack>
          </div>
        </div>
      </div>
    </div>
  )
}

export {Step7}
